import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthLayout } from "@/components/AuthLayout";
import { TextField } from "@/components/TextField";
import { SubmitButton } from "@/components/SubmitButton";
import { forgotPassword } from "@/lib/brokerAuth";
import { ApiError } from "@/lib/api";

// Step 1 of the forgotten-password flow: request a reset code. PMS's endpoint is
// deliberately non-enumerating (always succeeds), so we ALWAYS advance to the
// reset step with the same neutral notice — the UI never reveals whether an
// account exists. Only a genuine service outage shows an error.
export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      await forgotPassword(email);
      // Same outcome regardless of whether the email is registered.
      navigate("/reset-password", { state: { email, sent: true } });
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Something went wrong. Please try again.",
      );
    } finally {
      setPending(false);
    }
  }

  return (
    <AuthLayout
      title="Reset your password"
      description="Enter your email and we’ll send a reset code if an account exists."
      footer={
        <Link to="/login" className="text-brk-accent hover:underline">
          Back to sign in
        </Link>
      }
    >
      <form className="flex flex-col gap-4" onSubmit={onSubmit}>
        <TextField
          label="Email"
          type="email"
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        {error && (
          <p role="alert" className="text-sm text-brk-danger">
            {error}
          </p>
        )}
        <SubmitButton full pending={pending} pendingLabel="Sending…">
          Send reset code
        </SubmitButton>
        <Link
          to="/reset-password"
          state={{ email }}
          className="text-center text-sm text-brk-dim hover:underline"
        >
          I already have a code
        </Link>
      </form>
    </AuthLayout>
  );
}
